import { BrandChip } from '@/components/BrandChip';
import { HintTooltip } from '@/components/HintTooltip';
import { JsonViewer } from '@/components/JsonViewer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { brandResearchState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { formatDateTime } from '@/util/dates';
import logger from '@/util/logger';
import { isBrandResearchUnderway } from '@rock/types';
import type {
    Brand,
    BrandRequest,
    BrandResearch,
    BrandResearchProposal,
    ResearchedAlias,
    ResearchedDomain,
    ResearchedModel,
    UserId,
} from '@rock/types';
import { Loader2, Sparkles } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { applyBrandResearch, joinsOnApply, requestBrandResearch } from './brandResearchActions';

const log = logger('BrandResearchCard');

export type ApplyResolution = (label: string, action: (by: UserId) => Promise<Brand | undefined>) => void;

/**
 * AI research on a pending request: two web-searching models say what the name is, code checks every domain and
 * model page they cite, and the admin applies the proposal, or the parts of it they keep, through the same
 * resolve flows as the buttons above. Only a click starts a run: each one costs a few cents.
 */
export const BrandResearchCard: FC<{
    request: BrandRequest;
    brands: Brand[];
    by: UserId | undefined;
    disabled: boolean;
    onApply: ApplyResolution;
}> = ({ request, brands, by, disabled, onApply }) => {
    const runs = useAsyncAtomValue(
        useMemo(() => brandResearchState(request.id), [request.id]),
        [],
    );
    const latest = runs[0];
    const [starting, setStarting] = useState(false);
    const underway = !!latest && isBrandResearchUnderway(latest, new Date());
    const running = starting || underway;

    const start = async () => {
        if (!by) return;
        setStarting(true);
        try {
            await requestBrandResearch(request, by);
        } catch (err) {
            log.error('Brand research failed to start', { err, requestId: request.id });
            toast.error('Unable to research this name');
        } finally {
            setStarting(false);
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>Research</CardTitle>
                <CardDescription>
                    Two web-searching models look the name up, and every domain and model they cite is checked. About 5¢
                    a run. Nothing changes until you apply it.
                </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
                <Button variant="outline" disabled={disabled || running} onClick={() => void start()}>
                    {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {running ? 'Researching…' : latest ? 'Research Again' : 'Run AI Research'}
                </Button>
            </CardContent>
            {latest && !underway && (
                <CardContent className="space-y-4">
                    {latest.status === 'failed' && (
                        <div className="text-sm text-destructive">
                            Research failed: {latest.error ?? 'unknown error'}
                        </div>
                    )}
                    {(latest.status === 'requested' || latest.status === 'running') && (
                        <div className="text-sm text-destructive">This run didn’t finish. Research again to retry.</div>
                    )}
                    {latest.proposal && (
                        <ProposalView
                            key={latest.id}
                            request={request}
                            proposal={latest.proposal}
                            brands={brands}
                            disabled={disabled}
                            onApply={onApply}
                        />
                    )}
                    <RunDetails run={latest} />
                </CardContent>
            )}
        </Card>
    );
};

export const DECISION_LABELS: Record<BrandResearchProposal['decision'], string> = {
    'new-brand': 'New brand',
    'alias-of': 'Alias of',
    'line-of': 'Line of',
    'not-a-brand': 'Not a brand',
};

const ProposalView: FC<{
    request: BrandRequest;
    proposal: BrandResearchProposal;
    brands: Brand[];
    disabled: boolean;
    onApply: ApplyResolution;
}> = ({ request, proposal, brands, disabled, onApply }) => {
    const target = brands.find((brand) => brand.id === proposal.targetBrandId);
    const lands = proposal.decision !== 'not-a-brand';
    const [domains, setDomains] = useState(
        () => new Set(proposal.domains.filter((domain) => domain.verdict === 'ok').map((domain) => domain.host)),
    );
    const [aliases, setAliases] = useState(
        () => new Set(proposal.aliases.filter((alias) => startsTicked(alias)).map((alias) => alias.name)),
    );

    const apply = () => {
        const picks = { aliases: lands ? [...aliases] : [], domains: lands ? [...domains] : [] };
        onApply(applyLabel(request, proposal, target), async (by) => {
            const applied = await applyBrandResearch(request, proposal, picks, brands, by);
            if (applied.skipped.length > 0) toast.warning(applied.skipped.join(' '));
            return applied.brand;
        });
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                <Badge>{DECISION_LABELS[proposal.decision]}</Badge>
                <DecisionSubject proposal={proposal} target={target} />
                <AgreementBadge proposal={proposal} />
                {proposal.labelKind === 'marketplace-label' && <Badge variant="outline">Marketplace label</Badge>}
            </div>
            <p className="text-sm">{proposal.summary}</p>
            {proposal.decision === 'not-a-brand' && proposal.notABrandReason && (
                <p className="text-sm text-muted-foreground">{proposal.notABrandReason}</p>
            )}
            {(proposal.owner.company || proposal.owner.history) && (
                <Section title="Owner">
                    <div className="text-sm">
                        {[proposal.owner.company, proposal.owner.country].filter(Boolean).join(' · ')}
                    </div>
                    {proposal.owner.history && (
                        <div className="text-sm text-muted-foreground">{proposal.owner.history}</div>
                    )}
                </Section>
            )}
            {lands && proposal.domains.length > 0 && (
                <Section
                    title="Domains"
                    hint="Ticked ones join the brand's researched domains, which the manual gate trusts."
                >
                    {proposal.domains.map((domain) => (
                        <DomainRow
                            key={domain.host}
                            domain={domain}
                            brands={brands}
                            checked={domains.has(domain.host)}
                            onToggle={() => setDomains(toggled(domains, domain.host))}
                        />
                    ))}
                </Section>
            )}
            {lands && proposal.aliases.length > 0 && (
                <Section
                    title="Aliases"
                    hint="Ticked names resolve to the brand. A pending request ticked here settles too. A name only one provider gave starts unticked."
                >
                    {proposal.aliases.map((alias) => (
                        <AliasRow
                            key={alias.name}
                            alias={alias}
                            brands={brands}
                            addedOnApply={joinsOnApply(alias.name, request, proposal)}
                            checked={aliases.has(alias.name)}
                            onToggle={() => setAliases(toggled(aliases, alias.name))}
                        />
                    ))}
                </Section>
            )}
            {proposal.otherUsesOfName.length > 0 && (
                <Section
                    title="Others using the name"
                    hint="Facts only. If one makes equipment Rock tracks, a shared nameplate may be the answer."
                >
                    {proposal.otherUsesOfName.map((use) => (
                        <div key={use.company} className="text-sm">
                            <span className="font-medium">{use.company}</span>{' '}
                            <span className="text-muted-foreground">{use.products}</span>
                        </div>
                    ))}
                </Section>
            )}
            {proposal.exampleModels.length > 0 && (
                <Section title="Example models" hint="Evidence only; nothing is created from these.">
                    <div className="flex flex-wrap gap-2">
                        {proposal.exampleModels.map((model) => (
                            <ModelChip key={model.modelNumber} model={model} />
                        ))}
                    </div>
                </Section>
            )}
            <div className="flex justify-end">
                <Button disabled={disabled || !hasBrandToApply(proposal, target)} onClick={apply}>
                    Apply
                </Button>
            </div>
        </div>
    );
};

const DecisionSubject: FC<{ proposal: BrandResearchProposal; target: Brand | undefined }> = ({ proposal, target }) => {
    if (proposal.decision === 'new-brand') return <span className="font-medium">{proposal.brandName}</span>;
    if (proposal.decision === 'line-of') {
        const newBrand = proposal.targetBrandName && `${proposal.targetBrandName} (new)`;
        return (
            <span className="flex items-center gap-1 font-medium">
                {target ? <BrandChip brand={target} /> : newBrand}
                <span>{proposal.lineName}</span>
            </span>
        );
    }
    const belongsTo = target ? <BrandChip brand={target} /> : proposal.targetBrandName;
    if (proposal.decision === 'alias-of') return belongsTo ?? null;
    return belongsTo ? (
        <span className="flex items-center gap-1 text-sm text-muted-foreground">belongs to {belongsTo}</span>
    ) : null;
};

const AgreementBadge: FC<{ proposal: BrandResearchProposal }> = ({ proposal }) => {
    if (proposal.agreement === 'agreed') return <Badge variant="success">Both agree</Badge>;
    if (proposal.agreement === 'single') return <Badge variant="secondary">One answer</Badge>;
    return (
        <HintTooltip
            hint={proposal.alternatives
                .map((alt) => `${alt.provider}: ${DECISION_LABELS[alt.decision]} ${alt.brand}`)
                .join('. ')}
        >
            <Badge variant="destructive">Split</Badge>
        </HintTooltip>
    );
};

const VERDICT_VARIANT = { ok: 'success', doubtful: 'outline', rejected: 'destructive' } as const;

const DomainRow: FC<{ domain: ResearchedDomain; brands: Brand[]; checked: boolean; onToggle: () => void }> = ({
    domain,
    brands,
    checked,
    onToggle,
}) => {
    const owner = brands.find((brand) => brand.id === domain.ownerBrandId);
    const id = `research-domain-${domain.host}`;
    return (
        <div className="flex flex-wrap items-center gap-2 text-sm">
            <Checkbox id={id} checked={checked} disabled={domain.verdict === 'rejected'} onCheckedChange={onToggle} />
            <label htmlFor={id} className="font-mono">
                {domain.host}
            </label>
            <span className="text-muted-foreground">{domain.role}</span>
            <Badge variant={VERDICT_VARIANT[domain.verdict]}>
                {owner ? `already ${owner.canonicalName}’s` : domain.detail}
            </Badge>
            <Sources sources={domain.sources} />
            <a
                className="text-xs underline-offset-2 hover:underline"
                href={domain.evidenceUrl}
                target="_blank"
                rel="noreferrer"
            >
                evidence
            </a>
        </div>
    );
};

const AliasRow: FC<{
    alias: ResearchedAlias;
    brands: Brand[];
    addedOnApply: boolean;
    checked: boolean;
    onToggle: () => void;
}> = ({ alias, brands, addedOnApply, checked, onToggle }) => {
    const owner = brands.find((brand) => brand.id === alias.takenBy);
    const id = `research-alias-${alias.name}`;
    return (
        <div className="flex flex-wrap items-center gap-2 text-sm">
            <Checkbox id={id} checked={checked} disabled={!isPickable(alias)} onCheckedChange={onToggle} />
            <label htmlFor={id}>{alias.name}</label>
            <span className="text-muted-foreground">
                {alias.kind === 'pending-request' ? 'pending request' : alias.kind}
            </span>
            {alias.noop && (
                <Badge variant="secondary">{addedOnApply ? 'added on Apply' : 'already the brand’s name'}</Badge>
            )}
            {owner && <Badge variant="destructive">already {owner.canonicalName}</Badge>}
            <Sources sources={alias.sources} />
        </div>
    );
};

const MODEL_MARKS = { found: '✓', missing: '✗', blocked: '?' } as const;
const MODEL_HINTS = {
    found: 'The cited page prints this number.',
    missing: 'The cited page does not print this number.',
    blocked: 'We could not read the cited page.',
} as const;

const ModelChip: FC<{ model: ResearchedModel }> = ({ model }) => (
    <HintTooltip hint={`${model.productType}. ${MODEL_HINTS[model.check]}`}>
        <a href={model.evidenceUrl} target="_blank" rel="noreferrer">
            <Badge variant={model.check === 'found' ? 'outline' : 'secondary'} className="font-mono">
                {MODEL_MARKS[model.check]} {model.modelNumber}
            </Badge>
        </a>
    </HintTooltip>
);

const Sources: FC<{ sources: string[] }> = ({ sources }) => (
    <span className="text-xs text-muted-foreground">{sources.join(' + ')}</span>
);

const Section: FC<{ title: string; hint?: string; children: ReactNode }> = ({ title, hint, children }) => (
    <div className="space-y-1">
        <div className="text-sm font-medium">{title}</div>
        {hint && <div className="section-description">{hint}</div>}
        <div className="space-y-1">{children}</div>
    </div>
);

const RunDetails: FC<{ run: BrandResearch }> = ({ run }) => (
    <Accordion type="single" collapsible>
        <AccordionItem value="run">
            <AccordionTrigger className="text-sm">
                Run of {formatDateTime(run.requestedAt)} · ${run.costUsd.toFixed(3)}
                {run.version && ` · ${run.version}`}
            </AccordionTrigger>
            <AccordionContent className="space-y-2">
                {run.calls.map((call) => (
                    <div key={call.provider} className="text-sm">
                        <span className="font-medium">{call.provider}</span> {call.model} ·{' '}
                        {(call.ms / 1000).toFixed(0)}s · {call.searches} searches · ${call.costUsd.toFixed(3)}
                        {call.error && <span className="text-destructive"> · {call.error}</span>}
                    </div>
                ))}
                <JsonViewer data={run} />
            </AccordionContent>
        </AccordionItem>
    </Accordion>
);

function isPickable(alias: ResearchedAlias): boolean {
    return !alias.noop && !alias.takenBy;
}

/** Code checks an alias only against the catalog, so one starts ticked only when both providers gave it. */
function startsTicked(alias: ResearchedAlias): boolean {
    return isPickable(alias) && alias.sources.length > 1;
}

function toggled(set: ReadonlySet<string>, value: string): Set<string> {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
}

function applyLabel(request: BrandRequest, proposal: BrandResearchProposal, target: Brand | undefined): string {
    const targetName = target?.canonicalName ?? proposal.targetBrandName ?? '';
    if (proposal.decision === 'new-brand')
        return `“${request.brand}” resolved as ${proposal.brandName ?? request.brand}`;
    if (proposal.decision === 'alias-of') return `“${request.brand}” added as an alias of ${targetName}`;
    if (proposal.decision === 'line-of') return `“${request.brand}” is the ${proposal.lineName} line of ${targetName}`;
    return 'Marked as not a brand';
}

/** A catalog brand the proposal lands on must be loaded, and a line needs a brand to sit under. */
function hasBrandToApply(proposal: BrandResearchProposal, target: Brand | undefined): boolean {
    if (proposal.decision === 'not-a-brand') return true;
    if (proposal.targetBrandId) return !!target;
    return proposal.decision !== 'line-of' || !!proposal.targetBrandName;
}
